import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { UserSearchDialog } from '../components/UserSearchDialog';
import {
  MessageCircle,
  Send,
  Plus,
  ArrowLeft,
  Loader2,
} from 'lucide-react';
import { messageAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/LanguageContext';
import { formatRelativeTime, getAvatarFallback } from '../lib/utils';

export function MessagesPage() {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const { user, fetchUnreadCounts } = useAuth();
  const { t } = useTranslation();
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);

  const fetchConversations = useCallback(async () => {
    try {
      const res = await messageAPI.getConversations();
      setConversations(res.data.conversations || []);
    } catch (err) {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMessages = useCallback(async (convId) => {
    try {
      const res = await messageAPI.getConversation(convId);
      setActiveConv(res.data);
      setMessages(res.data.messages || []);
      await messageAPI.markRead(convId);
      fetchUnreadCounts();
    } catch (err) {
      // ignore
    }
  }, [fetchUnreadCounts]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  useEffect(() => {
    if (conversationId) {
      fetchMessages(conversationId);
      pollRef.current = setInterval(() => fetchMessages(conversationId), 5000);
      return () => clearInterval(pollRef.current);
    }
    return () => clearInterval(pollRef.current);
  }, [conversationId, fetchMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !conversationId) return;

    setSending(true);
    try {
      const res = await messageAPI.sendMessage(conversationId, { content: newMessage.trim() });
      setMessages(prev => [...prev, res.data]);
      setNewMessage('');
      fetchConversations();
    } catch (err) {
      // ignore
    } finally {
      setSending(false);
    }
  };

  const handleNewConversation = async (targetUser) => {
    try {
      const res = await messageAPI.createConversation(targetUser.user_id);
      const convId = res.data.conversation_id;
      fetchConversations();
      navigate(`/messages/${convId}`);
    } catch (err) {
      // ignore
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" style={{ height: 'calc(100vh - 180px)' }}>
          <div className="flex h-full">
            {/* Conversation List */}
            <div className={`w-full md:w-80 border-r border-slate-200 flex flex-col ${conversationId ? 'hidden md:flex' : 'flex'}`}>
              <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                <h2 className="font-bold text-slate-900">{t('messages.title')}</h2>
                <Button variant="ghost" size="sm" onClick={() => setSearchOpen(true)} data-testid="new-message-btn">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>

              <ScrollArea className="flex-1">
                {loading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
                  </div>
                ) : conversations.length > 0 ? (
                  <div className="divide-y divide-slate-50">
                    {conversations.map((conv) => (
                      <button
                        key={conv.conversation_id}
                        className={`w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors ${
                          conversationId === conv.conversation_id ? 'bg-orange-50 border-l-2 border-orange-500' : ''
                        }`}
                        onClick={() => navigate(`/messages/${conv.conversation_id}`)}
                      >
                        <Avatar className="w-10 h-10 flex-shrink-0">
                          <AvatarImage src={conv.other_user?.picture} />
                          <AvatarFallback className="bg-orange-100 text-orange-700 text-sm">
                            {getAvatarFallback(conv.other_user?.name)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="font-medium text-slate-900 text-sm truncate">{conv.other_user?.name}</p>
                            {conv.last_message_at && (
                              <span className="text-xs text-slate-400 flex-shrink-0">{formatRelativeTime(conv.last_message_at)}</span>
                            )}
                          </div>
                          <p className="text-xs text-slate-500 truncate">{conv.last_message_preview || 'No messages yet'}</p>
                        </div>
                        {conv.unread_count > 0 && (
                          <span className="bg-orange-500 text-white text-[10px] font-bold min-w-[20px] h-[20px] rounded-full flex items-center justify-center px-1 flex-shrink-0">
                            {conv.unread_count}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 px-4">
                    <MessageCircle className="w-10 h-10 mx-auto text-slate-300 mb-3" />
                    <p className="text-sm text-slate-500 mb-3">{t('messages.noConversations')}</p>
                    <Button variant="outline" size="sm" onClick={() => setSearchOpen(true)}>
                      {t('messages.startConversation')}
                    </Button>
                  </div>
                )}
              </ScrollArea>
            </div>

            {/* Message Thread */}
            <div className={`flex-1 flex flex-col ${!conversationId ? 'hidden md:flex' : 'flex'}`}>
              {conversationId && activeConv ? (
                <>
                  {/* Thread Header */}
                  <div className="p-4 border-b border-slate-100 flex items-center gap-3">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="md:hidden"
                      onClick={() => navigate('/messages')}
                    >
                      <ArrowLeft className="w-4 h-4" />
                    </Button>
                    <Avatar className="w-8 h-8">
                      <AvatarImage src={activeConv.other_user?.picture} />
                      <AvatarFallback className="bg-orange-100 text-orange-700 text-xs">
                        {getAvatarFallback(activeConv.other_user?.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium text-slate-900 text-sm">{activeConv.other_user?.name}</p>
                    </div>
                  </div>

                  {/* Messages */}
                  <ScrollArea className="flex-1 p-4">
                    <div className="space-y-3">
                      {messages.map((msg) => {
                        const isOwn = msg.sender_id === user?.user_id;
                        return (
                          <div key={msg.message_id} className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-[70%] px-4 py-2.5 ${
                              isOwn
                                ? 'bg-orange-500 text-white rounded-2xl rounded-br-sm'
                                : 'bg-slate-100 text-slate-900 rounded-2xl rounded-bl-sm'
                            }`}>
                              <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                              <p className={`text-[10px] mt-1 ${isOwn ? 'text-orange-200' : 'text-slate-400'}`}>
                                {formatRelativeTime(msg.created_at)}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                      <div ref={messagesEndRef} />
                    </div>
                  </ScrollArea>

                  {/* Message Input */}
                  <form onSubmit={handleSend} className="p-4 border-t border-slate-100 flex gap-2">
                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder={t('messages.typeMessage')}
                      className="flex-1"
                      maxLength={5000}
                    />
                    <Button type="submit" disabled={sending || !newMessage.trim()} className="btn-boost">
                      {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                  </form>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <MessageCircle className="w-16 h-16 mx-auto text-slate-200 mb-4" />
                    <p className="text-slate-500">{t('messages.chooseConversation')}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <UserSearchDialog
        open={searchOpen}
        onOpenChange={setSearchOpen}
        onSelect={handleNewConversation}
      />
    </div>
  );
}
